import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from './admin/useAdminApi';

/**
 * Which accounts we track unapplied payments for.
 *
 * ⚠ This list is UNFILTERED on purpose — it shows accounts that are switched
 * off, and accounts that are not portal customers at all. Every customer-facing
 * surface hides both; this is the screen you switch one back on from.
 */

export interface TrackedAccount {
  qbo_customer_id: string;
  name: string;
  customer_id: string | null;
  in_portal: boolean;
  payments: number;
  unapplied: number;
  oldest: string;
  tracking_enabled: boolean;
  note: string | null;
  updated_by: string | null;
  updated_at: string | null;
}

export interface TrackingTotals {
  accounts: number;
  unapplied: number;
  tracked_accounts: number;
  tracked_unapplied: number;
  phantoms_excluded: number;
}

interface TrackingPayload {
  ok: true;
  accounts: TrackedAccount[];
  totals: TrackingTotals;
}

export function usePaymentTracking() {
  return useQuery<TrackingPayload>({
    queryKey: ['payment-tracking'],
    queryFn: () => adminFetch('admin-payment-tracking'),
    staleTime: 60 * 1000,
  });
}

export function useSetPaymentTracking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { qbo_customer_id: string; enabled: boolean; note?: string }) =>
      adminFetch<TrackingPayload>('admin-payment-tracking', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: (data) => {
      qc.setQueryData(['payment-tracking'], data);
      // The switch changes what the customer panel and the chain rollup show.
      qc.invalidateQueries({ queryKey: ['unapplied-payments'] });
      qc.invalidateQueries({ queryKey: ['group-unapplied-payments'] });
    },
  });
}

/** The weekly "where should we apply this?" notice, run by hand. */
export function useRunUnappliedNotice() {
  return useMutation({
    mutationFn: (input: { dry_run: boolean }) =>
      adminFetch<Record<string, unknown>>('admin-unapplied-payment-notify', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
  });
}

/** The paper-statement pass. Defaults to preview server-side — it spends postage. */
export function useRunPaperStatements() {
  return useMutation({
    mutationFn: (input: { mode: 'preview' | 'send' }) =>
      adminFetch<Record<string, unknown>>('admin-unapplied-paper-statements', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
  });
}
